import { GoldButton } from "@/components/ui/button-variants";
import { Link } from "react-router-dom";
import { ScrollToTop } from "@/components/ui/scroll-to-top";
import { useLanguage } from "@/contexts/LanguageContext";
import heroBackground from "@/assets/hero-background.jpg";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SiteHeader } from "@/components/layout/SiteHeader";

const About = () => {
  const { language, setLanguage } = useLanguage();
  const lang = language;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader lang={lang} onToggleLang={() => setLanguage(lang === 'en' ? 'fr' : 'en')} />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-navy via-navy-2 to-navy-dark bg-cover bg-center relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
          <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
        </div>
        <div className="absolute inset-0 bg-navy/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground mb-6 leading-tight">
              {lang==='fr' ? (
                <>Nous ne faisons pas que des <span className="text-accent">transactions</span>.<br />
                Nous bâtissons des <span className="text-accent">héritages</span>.</>
              ) : (
                <>We don't just do <span className="text-accent">transactions</span>.<br />
                We build <span className="text-accent">legacies</span>.</>
              )}
            </h1>
            <p className="text-xl text-primary-foreground/90 mb-12 max-w-4xl mx-auto">
              {lang==='fr' 
                ? "Chez AcquizGroup, nous sommes des architectes de la succession d'entreprise. Notre mission est de transformer la transition la plus importante de votre carrière en une opportunité de croissance durable."
                : "At AcquizGroup, we are architects of business succession. Our mission is to facilitate the most important transition of your career into an opportunity for sustainable growth."}
            </p>
          </div>
        </div>
      </section>

      {/* Mission and Vision */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {lang==='fr' ? 'Notre Mission et Vision' : 'Our Mission and Vision'}
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-8 text-lg text-muted-foreground leading-relaxed">
            <p>
              {lang==='fr' 
                ? "Nous avons fondé AcquizGroup pour répondre à une triple urgence:"
                : "We founded AcquizGroup to address a triple urgency:"}
            </p>
            
            <div className="grid md:grid-cols-1 gap-6">
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="font-semibold text-foreground mb-3 text-accent">
                  {lang==='fr' ? 'Urgence Économique:' : 'Economic Urgency:'}
                </h3>
                <p>{lang==='fr' 
                  ? "De nombreuses PME ferment faute de successeurs, affaiblissant le tissu économique local et les communautés."
                  : "Many SMEs close due to lack of successors, weakening the local economic fabric and communities."}</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="font-semibold text-foreground mb-3 text-accent">
                  {lang==='fr' ? 'Urgence Démographique:' : 'Demographic Urgency:'}
                </h3>
                <p>{lang==='fr' 
                  ? "Le vieillissement de la population des propriétaires d'entreprise crée un vide générationnel."
                  : "The aging population of business owners is creating a generational void."}</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="font-semibold text-foreground mb-3 text-accent">
                  {lang==='fr' ? 'Intégration des Talents:' : 'Talent Integration:'}
                </h3>
                <p>{lang==='fr' 
                  ? "De nombreux professionnels qualifiés et entrepreneurs cherchent des solutions pour s'intégrer dans l'économie réelle en créant de la valeur."
                  : "Many qualified professionals and entrepreneurs are seeking solutions to integrate into the real economy by creating value."}</p>
              </div>
            </div>
            
            <p className="text-center font-medium text-foreground">
              {lang==='fr' 
                ? "Notre vision est simple: connecter des entrepreneurs motivés avec des entreprises à fort potentiel pour assurer la régénération économique locale. Nous créons un écosystème où chaque partie prenante — le vendeur, l'acheteur et les partenaires — peut prospérer."
                : "Our vision is simple: connect motivated entrepreneurs with high-potential businesses to ensure local economic regeneration. We create an ecosystem where every stakeholder — the seller, the buyer, and the partners — can thrive."}
            </p>
          </div>
        </div>
      </section>

      {/* Unique Approach */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {lang==='fr' ? 'Notre Approche Unique: Expertise Hybride' : 'Our Unique Approach: Hybrid Expertise'}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {lang==='fr' 
                ? "AcquizGroup se positionne au carrefour de plusieurs industries pour offrir une solution complète."
                : "AcquizGroup positions itself at the crossroads of several industries to offer a complete solution."}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-card p-8 rounded-lg border border-border">
              <h3 className="text-xl font-semibold text-primary mb-4">
                {lang==='fr' ? 'Expertise en Conseil Stratégique' : 'Strategic Consulting Expertise'}
              </h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Pour la structuration opérationnelle et l'accompagnement humain à chaque étape."
                  : "For operational structuring and human support at every stage."}
              </p>
            </div>
            
            <div className="bg-card p-8 rounded-lg border border-border">
              <h3 className="text-xl font-semibold text-primary mb-4">
                {lang==='fr' ? 'Plateforme Technologique' : 'Technology Platform'}
              </h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Un portail intelligent qui connecte vendeurs et acheteurs de manière fluide et sécurisée."
                  : "An intelligent portal that connects sellers and buyers in a fluid and secure manner."}
              </p>
            </div>
            
            <div className="bg-card p-8 rounded-lg border border-border">
              <h3 className="text-xl font-semibold text-primary mb-4">
                {lang==='fr' ? 'Stratégies d\'Expansion Internationale' : 'International Expansion Strategies'}
              </h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Une approche unique pour accompagner les acheteurs internationaux dans leur processus d'acquisition sur les marchés nord-américains."
                  : "A unique approach to accompany international buyers in their acquisition process in North American markets."}
              </p>
            </div>
            
            <div className="bg-card p-8 rounded-lg border border-border">
              <h3 className="text-xl font-semibold text-primary mb-4">
                {lang==='fr' ? 'Éducation et Formation' : 'Education and Training'}
              </h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Accès à des ressources et modules E-learning pour que chaque acteur comprenne et maîtrise les étapes de la transaction."
                  : "Access to resources and E-learning modules so that each actor understands and masters the transaction steps."}
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto">
              {lang==='fr' 
                ? "Nous agissons comme facilitateur, connecteur et partenaire de confiance pour des transitions réussies, de la première discussion à la signature finale."
                : "We act as a facilitator, a connector, and a trusted partner for successful transitions, from the first discussion to the final signature."}
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {lang==='fr' ? 'Nos Valeurs' : 'Our Values'}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-accent-foreground">{lang==='fr' ? 'C' : 'T'}</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">
                {lang==='fr' ? 'Confiance' : 'Trust'}
              </h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Nous construisons chaque relation sur la transparence et la confidentialité, en veillant à ce que vos informations sensibles soient toujours protégées."
                  : "We build every relationship on transparency and confidentiality, ensuring that your sensitive information is always protected."}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-foreground">R</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">
                {lang==='fr' ? 'Rigueur' : 'Rigor'}
              </h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Notre processus est structuré et notre méthodologie rigoureuse, pour une transaction sécurisée et efficace."
                  : "Our process is structured and our methodology is rigorous, for a secure and efficient transaction."}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-accent-foreground">{lang==='fr' ? 'É' : 'H'}</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">
                {lang==='fr' ? 'Écoute Humaine' : 'Human Listening'}
              </h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Au-delà des chiffres, nous comprenons les enjeux personnels et le parcours de chaque entrepreneur."
                  : "Beyond the numbers, we understand the personal stakes and the journey of each entrepreneur."}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-foreground">I</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Impact</h3>
              <p className="text-muted-foreground">
                {lang==='fr' 
                  ? "Chaque transaction est pour nous une opportunité de créer un impact positif durable, pour l'économie et pour les individus."
                  : "Every transaction is for us an opportunity to create a lasting positive impact, for the economy and for individuals."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {lang==='fr' ? 'Prêt à Passer à l\'Étape Suivante?' : 'Ready to Take the Next Step?'}
          </h2>
          <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto">
            {lang==='fr' 
              ? "Que vous souhaitiez vendre votre entreprise, en acquérir une ou devenir un partenaire stratégique, votre opportunité commence ici."
              : "Whether you want to sell your business, acquire one, or become a strategic partner, your opportunity starts here."}
          </p>
          <Link to="/">
            <GoldButton size="lg">
              {lang==='fr' ? 'Retour à l\'Accueil' : 'Back to Homepage'}
            </GoldButton>
          </Link>
        </div>
      </section>

      <SiteFooter lang={lang} />
      <ScrollToTop />
    </div>
  );
};

export default About;
